import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
} from "@tanstack/react-query";
import * as React from "react";
import { api } from "@/lib/api";
import {
  endpoints,
  type DirectConversationListResponse,
  type DirectConversationSummary,
  type DirectMessageListResponse,
  type DirectMessageReadResponse,
  type DirectMessageSendResponse,
  type MessageAttachmentInput,
  type MessageResponse,
} from "@/lib/endpoints";
import { queryKeys } from "@/lib/query-keys";

/**
 * The private half of the messenger — one conversation between two of §2's
 * people, behind the SAME `messages` flag and the `message.direct` capability.
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * EVERY DECISION `use-messages.ts` ARGUED IS INHERITED HERE ON PURPOSE, AND THE
 * TWO PLACES IT DIFFERS ARE THE TWO PLACES THE FEATURE GENUINELY DIFFERS.
 *
 * INHERITED, WITHOUT RE-ARGUING: polling rather than sockets; thirty seconds
 * because the other half of a conversation is a person waiting; the poll stops
 * when the tab is hidden; the poll stops once the reader has paged back, because
 * refetching an infinite query refetches EVERY loaded page; no optimistic
 * bubble, because there is no send queue and a message on screen that may never
 * exist is the one failure a written-down conversation cannot afford.
 *
 * DIFFERENT 1 — READ STATE IS PER CONVERSATION. The room has one watermark per
 * person; this has one per PAIR. A global mark would clear the CA's unanswered
 * question because the son's message happened to be newer, so the id in
 * {@link useMarkDirectRead} travels with the peer it belongs to and the
 * per-conversation `unread` on each list row is what the screen renders.
 *
 * DIFFERENT 2 — THE LIST IS ALSO THE DIRECTORY, AND IT IS ALSO THE BADGE. There
 * is no `/messages/direct/unread-count` to poll: the only list of people this
 * caller may write to is `GET /messages/direct` itself, and it is three rows.
 * So the one request answers "who can I write to", "what is waiting in each"
 * and "what number goes on the header control" — see {@link useDirectUnread},
 * which shares this query's cache entry rather than opening a second one.
 * ═════════════════════════════════════════════════════════════════════════════
 */
const DIRECT_POLL_MS = 30_000;

/** See `use-messages.ts` — one page polls, several do not. */
function loadedPages(data: { pages: readonly unknown[] } | undefined): number {
  return data?.pages.length ?? 1;
}

/**
 * The conversation list, and the people directory it doubles as.
 *
 * `enabled` IS PASSED IN RATHER THAN READ HERE, exactly as the room's unread
 * hook takes it: the capability check lives in the component beside the thing it
 * hides, and the hook can be exercised on its own. Called with `false` it makes
 * NO request — a person without `message.direct` must not poll a route that
 * would 403 him twice a minute, and `requireCap` writes every one of those
 * denials to `audit_log` (§2.1).
 */
export function useDirectConversations(enabled = true) {
  return useQuery({
    queryKey: queryKeys.messages.direct.list(),
    queryFn: () => api.get<DirectConversationListResponse>(endpoints.messagesDirect),
    enabled,
    refetchInterval: DIRECT_POLL_MS,
    /*
     * A conversation list whose unread counts are stale is worse than none: its
     * whole job is to say which of three people is waiting for an answer. Zero
     * overrides the global 60s staleTime, exactly as the room's badge does.
     */
    staleTime: 0,
  });
}

export interface DirectUnread {
  /** Across every private conversation. Zero while loading — never a flash. */
  unread: number;
  /** False when the module is off, the request failed, or nobody is asking. */
  available: boolean;
}

/**
 * The private half of the header badge.
 *
 * IT SHARES {@link useDirectConversations}' CACHE ENTRY rather than opening a
 * second query — same key, so react-query dedupes it to one request and one
 * interval no matter how many components ask. That is why the header polling a
 * three-row list costs the same as polling a count would.
 *
 * IT READS THE SERVER'S OWN TOTAL, not a sum over the rows. The server computes
 * `unread` from the watermarks it owns; re-deriving it here would be a second
 * answer to the same question, and the two would differ the moment a
 * conversation is filtered out of the list for any reason.
 */
export function useDirectUnread(enabled: boolean): DirectUnread {
  const query = useDirectConversations(enabled);

  return {
    unread: query.data?.unread ?? 0,
    available: enabled && query.isSuccess,
  };
}

/** The rows, or none at all while the list is loading or the module is off. */
export function directConversations(
  data: DirectConversationListResponse | undefined,
): DirectConversationSummary[] {
  return data?.conversations ?? [];
}

/** One row by peer id — the heading needs a name before the thread has loaded. */
export function findConversation(
  data: DirectConversationListResponse | undefined,
  peerUserId: number | null,
): DirectConversationSummary | null {
  if (peerUserId === null) return null;
  return directConversations(data).find((row) => row.peerUserId === peerUserId) ?? null;
}

export type DirectThreadQuery = UseInfiniteQueryResult<
  InfiniteData<DirectMessageListResponse>,
  Error
>;

/**
 * One conversation, newest page first and "load older" behind it.
 *
 * PAGES BACKWARDS, like the room's: page one is the NEWEST fifty, and
 * `fetchNextPage` walks into the past with a `before` cursor, so
 * `getNextPageParam` reads the OLDEST id in the page it was handed rather than
 * the newest. {@link flattenDirectMessages} then reverses the PAGES so the
 * screen renders oldest-first.
 *
 * `peerUserId` OF NULL MAKES NO REQUEST. The screen is reachable with nobody
 * selected — the list is the point of arrival — and a query keyed on a peer
 * that does not exist yet must not fire, not least because `/messages/direct/`
 * with an empty segment is a different route.
 */
export function useDirectMessages(peerUserId: number | null): DirectThreadQuery {
  const query = useInfiniteQuery({
    // Non-null asserted only inside `enabled`'s guard — the key still needs a
    // number, and -1 is never a real user id.
    queryKey: queryKeys.messages.direct.thread(peerUserId ?? -1),
    queryFn: ({ pageParam }) =>
      api.get<DirectMessageListResponse>(endpoints.messagesDirectWith(peerUserId ?? -1), {
        query: { before: pageParam ?? undefined },
      }),
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore && lastPage.messages.length > 0 ? lastPage.messages[0]?.id : undefined,
    enabled: peerUserId !== null,
    refetchInterval: (live) => (loadedPages(live.state.data) > 1 ? false : DIRECT_POLL_MS),
    refetchOnWindowFocus: (live) => loadedPages(live.state.data) <= 1,
    staleTime: 0,
  });

  /*
   * THE COMPENSATING HALF OF THE TWO GATES ABOVE, and it is not optional. The
   * room pairs the very same predicates with `useNewMessageSignal`; copying only
   * the gates left a paged-back private thread with NO path back to fresh at all
   * — no poll, no focus refetch, nothing but the reader's own send. The row
   * beside it kept polling, so the preview and the unread pill moved while the
   * transcript in the same viewport stayed where it was, and mark-read could not
   * fire either because the newest id on screen never changed.
   */
  useNewDirectMessageSignal(peerUserId, loadedPages(query.data) > 1);

  return query;
}

/**
 * Keeps a paged-back private thread live without polling it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE LIST IS ALREADY THE CHEAP "HAS ANYTHING HAPPENED" — it polls every thirty
 * seconds, ungated, and it is the same cache entry the screen beside this is
 * rendering, so watching it costs no request at all.
 *
 * IT WATCHES THE NEWEST MESSAGE ID IN THIS CONVERSATION, NOT ITS UNREAD COUNT.
 * The room had only a count to watch; here the row carries `lastMessage`, and an
 * id is the better signal in both directions: it moves when the other person
 * writes, which is the event a stopped thread must not miss, and it does NOT
 * move when the reader marks the conversation read — a count would fall to zero
 * there and spend a full multi-page refetch on the reader's own act of reading.
 *
 * SEEDING, NOT FIRING, ON THE FIRST VALUE, and re-seeding when the peer changes:
 * the first id observed for a conversation is recorded rather than treated as a
 * change, because the mount's own fetch has already been made. Comparing across
 * a peer switch would otherwise invalidate a thread that had just loaded.
 *
 * `active` IS THE POLL'S OWN STATE, INVERTED. While the thread is polling there
 * is nothing for this to add.
 * ─────────────────────────────────────────────────────────────────────────────
 */
function useNewDirectMessageSignal(peerUserId: number | null, active: boolean): void {
  const queryClient = useQueryClient();
  // Same key as the screen's own list, so react-query serves it from that one
  // request and interval rather than opening a second.
  const list = useDirectConversations(peerUserId !== null);
  const newestId = findConversation(list.data, peerUserId)?.lastMessage?.id ?? null;
  const available = peerUserId !== null && list.isSuccess;
  const lastSeenRef = React.useRef<{ peerUserId: number; newestId: number | null } | null>(null);

  React.useEffect(() => {
    if (!available || peerUserId === null) return;

    const previous = lastSeenRef.current;
    lastSeenRef.current = { peerUserId, newestId };

    if (previous === null || previous.peerUserId !== peerUserId) return;
    if (previous.newestId === newestId) return;
    if (!active) return;

    void queryClient.invalidateQueries({
      queryKey: queryKeys.messages.direct.thread(peerUserId),
    });
  }, [available, newestId, active, peerUserId, queryClient]);
}

/**
 * Every loaded message in this conversation, OLDEST FIRST.
 *
 * Page 1 is the newest, page 2 the one before it, so the PAGES are reversed and
 * their contents — already oldest-first within a page, by the server's contract
 * — are concatenated. Stated here rather than in the component so the ordering
 * rule has one home, as the room's does.
 */
export function flattenDirectMessages(
  data: InfiniteData<DirectMessageListResponse> | undefined,
): MessageResponse[] {
  if (data === undefined) return [];
  return [...data.pages].reverse().flatMap((page) => page.messages);
}

/** The freshest counts and the peer's name — they ride on page 1, `pages[0]`. */
export function directThreadFacts(data: InfiniteData<DirectMessageListResponse> | undefined): {
  total: number;
  unread: number;
  peerName: string | null;
} {
  const newest = data?.pages[0];
  return {
    total: newest?.total ?? 0,
    unread: newest?.unread ?? 0,
    peerName: newest?.peerName ?? null,
  };
}

export interface SendDirectVariables {
  body: string;
  attachment?: MessageAttachmentInput;
}

/**
 * Say something to one person.
 *
 * THE PEER IS BOUND WHEN THE HOOK IS CALLED, NOT PASSED AT SUBMIT TIME. The
 * recipient of a private message is the single thing that must not be able to
 * come out wrong, and a mutation whose variables carry it could be handed a
 * peer the composer had already moved on from — a message to the CA landing in
 * the son's thread. Binding it to the mounted thread makes that unrepresentable;
 * switching conversations remounts with a different peer.
 *
 * NO OPTIMISTIC BUBBLE, for the room's reason: there is no send queue, so a
 * bubble that appears before the server has the row can be a sentence the other
 * person will never see, read back by its author as if it had been delivered.
 *
 * ON SUCCESS THE WHOLE `["messages"]` PREFIX IS INVALIDATED — this thread, the
 * conversation list beside it (whose last line and ordering just changed) and
 * the header count move together. It cannot touch `["notifications"]`, and must
 * not: sending a message is not a compliance alert.
 */
export function useSendDirectMessage(peerUserId: number | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: SendDirectVariables) => {
      if (peerUserId === null) {
        // Unreachable from the screen — the composer is not mounted without a
        // peer. Rejecting rather than posting to a guessed id is the safe half
        // of "the recipient is never inferred".
        return Promise.reject(new Error("No conversation is open"));
      }
      return api.post<DirectMessageSendResponse>(endpoints.messagesDirectWith(peerUserId), {
        body: variables.body,
        ...(variables.attachment === undefined ? {} : { attachment: variables.attachment }),
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.messages.all });
    },
  });
}

/**
 * Move MY watermark in THIS conversation to the newest message on screen.
 *
 * FIRE AND FORGET, as the room's is: the reader is reading, and blocking
 * anything on a round trip so a badge can clear trades the thing they came for
 * against bookkeeping. A failure leaves the count up, which is the safe
 * direction — a badge shown twice is a nuisance, a badge silently cleared hides
 * a message nobody sees again, and there is no "mark unread" anywhere here.
 *
 * THE SERVER NEVER MOVES IT BACKWARDS, so a replayed or out-of-order call with
 * a stale id cannot resurrect a cleared badge.
 *
 * IT INVALIDATES THE LIST, NOT THE THREAD. The number that just changed is the
 * one on the row beside the reader and the one in the header; the thread's own
 * messages did not change, and refetching them would be a page of bodies
 * downloaded to learn one integer.
 */
export function useMarkDirectRead(peerUserId: number | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (upToId: number) => {
      if (peerUserId === null) return Promise.reject(new Error("No conversation is open"));
      return api.post<DirectMessageReadResponse>(endpoints.messagesDirectRead(peerUserId), {
        upToId,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.messages.direct.list() });
    },
  });
}
